import { Link } from "react-router-dom";
import { Building2, Check } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { SCHOOLS_OFFERINGS } from "../lib/site";

export default function ForSchools() {
  return (
    <>
      <PageHero
        label="For Schools"
        title={<>Partner with BTSA to <em className="font-italic-accent italic gold-text">transform</em> your students' voices.</>}
        subtitle="A proposed student development initiative for partner schools — moving students from participation to leadership, and building confident communicators who represent their school with pride."
      />

      {/* PARTICIPATION TO LEADERSHIP */}
      <section data-testid="school-progression" className="ivory-section py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <Reveal>
              <SectionLabel>The School Journey</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl lg:text-5xl font-light leading-[1.05]">
                Moving from participation to
                <span className="gold-text italic"> leadership.</span>
              </h2>
              <p className="mt-6 font-sans text-base lg:text-lg leading-relaxed">
                Beyond The Stage helps students move from simply speaking on stage
                to communicating with confidence in every aspect of life.
              </p>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/5 border border-black/5">
            {[
              { t: "Memorising", d: "Students learn and absorb content with structure and care." },
              { t: "Performing", d: "Students participate confidently and step onto the stage." },
              { t: "Communicating", d: "Students express ideas clearly and effectively." },
              { t: "Leading", d: "Students influence, inspire and represent their school." },
            ].map((s, i) => (
              <Reveal key={s.t} delay={i * 0.06}>
                <div data-testid={`school-step-${i}`} className="lux-card border-0 p-8 h-full">
                  <div className="font-serif text-3xl gold-text">0{i + 1}</div>
                  <h3 className="mt-5 font-serif text-xl font-light uppercase tracking-wide">{s.t}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12 max-w-2xl">
            <Reveal>
              <SectionLabel>What We Offer Schools</SectionLabel>
              <h2 className="mt-6 font-serif text-3xl lg:text-4xl text-white font-medium uppercase tracking-wide leading-tight">More than activities. Life-changing skills.</h2>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {SCHOOLS_OFFERINGS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.06}>
              <article data-testid={`school-offering-${i}`} className="lux-card p-9 h-full">
                <div className="flex items-center gap-4">
                  <Building2 size={20} className="text-[#d4af37]" />
                  <span className="section-label">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-serif text-2xl text-white font-medium uppercase tracking-wide leading-snug">{o.title}</h3>
                <p className="mt-4 font-sans text-base text-white/65 leading-relaxed font-light">{o.desc}</p>
              </article>
            </Reveal>
          ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Benefits for Schools</SectionLabel>
              <h2 className="mt-6 font-serif text-3xl lg:text-4xl text-white font-medium uppercase tracking-wide leading-tight">A measurable transformation</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {[
                "Higher student participation in school assemblies and showcases",
                "A trained pool of student presenters, emcees and leaders",
                "Visible improvement in classroom communication culture",
                "A signature annual showcase that elevates the school's brand",
                "Internationally recognised Trinity College London pathway integration",
                "Customised programs aligned to your school's calendar",
              ].map((b, i) => (
                <Reveal key={b} delay={i * 0.04}>
                  <li className="flex items-start gap-4 border-b border-white/10 pb-5">
                    <Check size={16} className="text-[#d4af37] mt-1 shrink-0" />
                    <span className="font-sans text-base text-white/80 font-light">{b}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <div className="flex justify-center"><SectionLabel>Partner With Us</SectionLabel></div>
            <h2 className="mt-6 font-serif text-3xl lg:text-5xl text-white font-medium uppercase tracking-wide leading-tight">Request a proposal</h2>
            <p className="mt-6 font-sans text-base lg:text-lg text-white/65 font-light max-w-xl mx-auto">
              Tell us about your school and goals. Our team will prepare a curated, tailored partnership proposal within three working days.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-5">
              <Link to="/contact" className="btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 font-semibold inline-block">Request A Proposal</Link>
              <Link to="/join" className="font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all duration-500 font-semibold inline-block">Speak With Us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
